import React, { useState } from 'react';
import { 
  Zap, 
  Users, 
  BarChart3, 
  ArrowRight,
  CheckCircle,
  Sparkles
} from 'lucide-react';
import { useOnboarding } from '../../../contexts/OnboardingContext';

export const WelcomeStep: React.FC = () => {
  const { startOnboarding, state } = useOnboarding();
  const [selectedUserType, setSelectedUserType] = useState<'developer' | 'business' | 'analyst' | null>(null);

  const userTypes = [
    {
      id: 'developer' as const,
      title: 'Developer',
      description: 'Build and integrate applications with MCP',
      icon: Zap,
      features: [
        'Technical integration tutorials',
        'Advanced configuration options',
        'API documentation focus',
        'Custom workflow creation'
      ],
      color: 'blue'
    },
    {
      id: 'business' as const,
      title: 'Business User',
      description: 'Connect tools to streamline workflows',
      icon: Users,
      features: [
        'No-code setup guidance',
        'Pre-built templates',
        'Business process focus',
        'Quick wins and productivity'
      ],
      color: 'green'
    },
    {
      id: 'analyst' as const,
      title: 'Data Analyst',
      description: 'Analyze data and monitor integrations',
      icon: BarChart3,
      features: [
        'Analytics and reporting focus',
        'Data visualization tutorials',
        'Monitoring and insights',
        'Performance optimization'
      ],
      color: 'purple'
    }
  ];

  const handleUserTypeSelect = (userType: 'developer' | 'business' | 'analyst') => {
    setSelectedUserType(userType);
    // Auto-start onboarding after selection
    setTimeout(() => {
      startOnboarding(userType);
    }, 500);
  };

  const getColorClasses = (color: string, isSelected: boolean) => {
    const base = isSelected ? 'ring-2 ring-offset-2' : 'hover:shadow-lg';
    
    switch (color) {
      case 'blue':
        return `${base} ${isSelected ? 'ring-blue-500 bg-blue-50 dark:bg-blue-900/20' : 'hover:bg-blue-50 dark:hover:bg-blue-900/10'} border-blue-200 dark:border-blue-800`;
      case 'green':
        return `${base} ${isSelected ? 'ring-green-500 bg-green-50 dark:bg-green-900/20' : 'hover:bg-green-50 dark:hover:bg-green-900/10'} border-green-200 dark:border-green-800`;
      case 'purple':
        return `${base} ${isSelected ? 'ring-purple-500 bg-purple-50 dark:bg-purple-900/20' : 'hover:bg-purple-50 dark:hover:bg-purple-900/10'} border-purple-200 dark:border-purple-800`;
      default:
        return base;
    }
  };

  const getIconColorClasses = (color: string) => {
    switch (color) {
      case 'blue':
        return 'text-blue-600 dark:text-blue-400';
      case 'green':
        return 'text-green-600 dark:text-green-400';
      case 'purple':
        return 'text-purple-600 dark:text-purple-400';
      default:
        return 'text-gray-600 dark:text-gray-400';
    }
  };

  return (
    <div className="space-y-8">
      {/* Hero Section */}
      <div className="text-center space-y-4">
        <div className="flex justify-center mb-4 animate-scale-in">
          <div className="relative">
            <div className="w-16 h-16 bg-gradient-to-br from-blue-500 to-purple-600 rounded-2xl flex items-center justify-center">
              <Sparkles className="w-8 h-8 text-white" />
            </div>
            <div className="absolute -inset-2 bg-gradient-to-r from-blue-500 via-purple-500 to-pink-500 rounded-2xl opacity-20 blur-sm animate-spin-slow" />
          </div>
        </div>

        <h1 className="text-3xl font-bold text-gray-900 dark:text-white animate-fade-in-up-delay-1">
          Welcome to MCP Platform
        </h1>

        <p className="text-lg text-gray-600 dark:text-gray-300 max-w-2xl mx-auto animate-fade-in-up-delay-2">
          Connect 2,500+ applications through the Model Context Protocol and supercharge your AI assistant with powerful integrations.
        </p>

        <div className="flex items-center justify-center gap-6 text-sm text-gray-500 dark:text-gray-400 animate-fade-in-up-delay-3">
          <div className="flex items-center gap-2">
            <CheckCircle className="w-4 h-4 text-green-500" />
            <span>No credit card required</span>
          </div>
          <div className="flex items-center gap-2">
            <CheckCircle className="w-4 h-4 text-green-500" />
            <span>5-minute setup</span>
          </div>
          <div className="flex items-center gap-2">
            <CheckCircle className="w-4 h-4 text-green-500" />
            <span>Works with Claude Desktop</span>
          </div>
        </div>
      </div>

      {/* User Type Selection */}
      <div className="space-y-4">
        <h2 className="text-xl font-semibold text-gray-900 dark:text-white text-center">
          Choose your path to get started
        </h2>
        
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {userTypes.map((userType, index) => {
            const Icon = userType.icon;
            const isSelected = selectedUserType === userType.id;
            
            return (
              <button
                key={userType.id}
                onClick={() => handleUserTypeSelect(userType.id)}
                className={`
                  p-6 border-2 rounded-xl text-left transition-all duration-200 cursor-pointer hover:scale-105 active:scale-95 animate-fade-in-up
                  ${getColorClasses(userType.color, isSelected)}
                `}
                style={{ animationDelay: `${0.6 + index * 0.1}s` }}
              >
                <div className="space-y-4">
                  <div className="flex items-center gap-3">
                    <div className={`p-2 rounded-lg bg-white dark:bg-gray-800 shadow-sm`}>
                      <Icon className={`w-6 h-6 ${getIconColorClasses(userType.color)}`} />
                    </div>
                    <div>
                      <h3 className="font-semibold text-gray-900 dark:text-white">
                        {userType.title}
                      </h3>
                      <p className="text-sm text-gray-600 dark:text-gray-400">
                        {userType.description}
                      </p>
                    </div>
                  </div>
                  
                  <ul className="space-y-2">
                    {userType.features.map((feature, featureIndex) => (
                      <li key={featureIndex} className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-400">
                        <CheckCircle className="w-3 h-3 text-green-500 flex-shrink-0" />
                        <span>{feature}</span>
                      </li>
                    ))}
                  </ul>

                  {isSelected && (
                    <div className="flex items-center gap-2 text-sm font-medium text-blue-600 dark:text-blue-400 animate-scale-in">
                      <span>Starting your personalized tour...</span>
                      <div className="animate-bounce-x">
                        <ArrowRight className="w-4 h-4" />
                      </div>
                    </div>
                  )}
                </div>
              </button>
            );
          })}
        </div>
      </div>

      {/* Additional Info */}
      <div className="text-center space-y-2 animate-fade-in-delay-4">
        <p className="text-sm text-gray-500 dark:text-gray-400">
          Don't worry, you can always change your preferences later or restart the onboarding.
        </p>
        <p className="text-xs text-gray-400 dark:text-gray-500">
          This tutorial will take approximately 5-7 minutes to complete.
        </p>
      </div>
    </div>
  );
};